import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, useNavigate, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { MailComposeEditor } from "@/components/MailComposeEditor";
import { useAuth } from "@/lib/auth";
import { type MailAccount } from "@/lib/api";
import { fetchMailAccounts, mailConnected } from "@/lib/googleConnect";
import { HUB_VERSION } from "@/lib/version";
import {
  isAccountFolderSyncEnabled,
  MAIL_SYNC_FOLDERS_EVENT,
  setAccountFolderSyncEnabled,
} from "@/mail/accountFolders";
import { resolveAccountLabel } from "@/mail/connectionLabels";
import { DEFAULT_MAIL_CACHE_WINDOW, readMailCacheWindowId } from "@/mail/offlineCache";
import {
  MAIL_VIEW_OPTIONS,
  MAIL_THREAD_ORDER_OPTIONS,
  MAIL_SWIPE_ACTIONS,
  MAIL_CLOCK_FORMAT_OPTIONS,
  MAIL_LINE_SPACING_OPTIONS,
  readMailClockFormat,
  readMailLineSpacing,
  readMailConversations,
  readMailSwipeLeft,
  readMailSwipeRight,
  readMailThreadOrder,
  readMailView,
  writeMailClockFormat,
  writeMailLineSpacing,
  writeMailConversations,
  writeMailSwipeLeft,
  writeMailSwipeRight,
  writeMailThreadOrder,
  writeMailView,
  type MailClockFormatId,
  type MailLineSpacingId,
  type MailSwipeActionId,
  type MailThreadOrderId,
  type MailViewId,
} from "@/mail/prefs";
import { MAIL_SIGNATURE_EVENT, readMailSignatureEnabled, signatureEditorPair } from "@/mail/signature";
import { saveMailSignatureEnabled, saveMailSignaturePair } from "@/mail/signatureSync";

const WEEKDAYS = [
  { id: 0, label: "Sunday" },
  { id: 1, label: "Monday" },
  { id: 2, label: "Tuesday" },
  { id: 3, label: "Wednesday" },
  { id: 4, label: "Thursday" },
  { id: 5, label: "Friday" },
  { id: 6, label: "Saturday" },
] as const;

const FOLDER_VIEWS = MAIL_VIEW_OPTIONS;
const THREAD_ORDERS = MAIL_THREAD_ORDER_OPTIONS;

const CACHE_WINDOWS = [
  { id: "1m", label: "30 Days" },
  { id: "3m", label: "90 Days" },
  { id: "6m", label: "6 Months" },
  { id: "12m", label: "1 Year" },
  { id: "24m", label: "2 Years" },
  { id: "36m", label: "3 Years" },
] as const;

const THEMES = [
  { id: "light", label: "Light" },
  { id: "dark", label: "Dark" },
  { id: "system", label: "System" },
] as const;

type ThemeId = (typeof THEMES)[number]["id"];

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function readString(key: string, fallback: string, allowed?: string[]) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    if (allowed && !allowed.includes(raw)) return fallback;
    return raw;
  } catch {
    return fallback;
  }
}

function applyTheme(theme: ThemeId) {
  localStorage.setItem("atw-hub-theme", theme);
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const dark = theme === "dark" || (theme === "system" && prefersDark);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
  document.documentElement.classList.toggle("dark", dark);
}

/** @deprecated Prefer SettingsLayout + area routes. */
export function SettingsPage() {
  return <SettingsProfilePage />;
}

export function SettingsProfilePage() {
  const { user } = useAuth();
  const [theme, setTheme] = useState<ThemeId>(() => {
    const t = readString("atw-hub-theme", "system", ["light", "dark", "system"]);
    return t as ThemeId;
  });
  const [clockFormat, setClockFormat] = useState<MailClockFormatId>(() => readMailClockFormat());
  const [notice, setNotice] = useState("");

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const profileName = user?.displayName || user?.name || "Signed-in user";
  const profileEmail = user?.email || "—";

  return (
    <div className="settings-area">
      <h2 className="settings-area-title">Profile</h2>
      {notice ? <p className="banner-ok">{notice}</p> : null}

      <section className="settings-card" id="profile">
        <div className="settings-card-head">
          <h3>Account</h3>
        </div>
        <dl className="settings-profile-grid">
          <div>
            <dt>Name</dt>
            <dd>{profileName}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{profileEmail}</dd>
          </div>
          <div>
            <dt>Title</dt>
            <dd>Member</dd>
          </div>
          <div>
            <dt>Organization</dt>
            <dd>ATW.Net</dd>
          </div>
        </dl>
      </section>

      <section className="settings-card" id="appearance">
        <div className="settings-card-head">
          <h3>Appearance</h3>
        </div>
        <div className="segmented" role="radiogroup" aria-label="Theme">
          {THEMES.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={theme === opt.id}
              className={theme === opt.id ? "active" : ""}
              onClick={() => setTheme(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="note">Light, Dark, and System are saved in this browser.</p>
      </section>

      <section className="settings-card" id="time-format">
        <div className="settings-card-head">
          <h3>Time format</h3>
        </div>
        <p className="muted">
          Mail list times use this format when the message is from today. Older messages show the
          date as MMM DD.
        </p>
        <div className="segmented" role="radiogroup" aria-label="Time format">
          {MAIL_CLOCK_FORMAT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={clockFormat === opt.id}
              className={clockFormat === opt.id ? "active" : ""}
              onClick={() => {
                setClockFormat(opt.id);
                writeMailClockFormat(opt.id);
                setNotice(
                  opt.id === "24h"
                    ? "Time format updated — 24-hour clock."
                    : "Time format updated — 12-hour AM/PM.",
                );
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="note">Example today: 2:05 PM or 14:05. Older: Sep 25.</p>
      </section>

      <section className="settings-card" id="this-mac">
        <div className="settings-card-head">
          <h3>This Mac</h3>
        </div>
        <p>API is running on port 8787.</p>
        <p className="note">
          Mail you send, chat you post, and connector changes last until the API process stops.
          They are not sent to a provider unless live OAuth is finished later.
        </p>
        <p className="muted tight">{HUB_VERSION}</p>
      </section>
    </div>
  );
}

export function SettingsNotificationsPage() {
  const [desktopAlerts, setDesktopAlerts] = useState(
    () => readString("atw-hub-alerts", "on") !== "off",
  );
  const [morningDigest, setMorningDigest] = useState(
    () => readString("atw-hub-digest", "off") === "on",
  );

  return (
    <div className="settings-area">
      <h2 className="settings-area-title">Notifications</h2>

      <section className="settings-card" id="notifications-alerts">
        <div className="settings-card-head">
          <h3>Alerts</h3>
        </div>
        <label className="settings-check">
          <input
            type="checkbox"
            checked={desktopAlerts}
            onChange={(e) => {
              setDesktopAlerts(e.target.checked);
              localStorage.setItem("atw-hub-alerts", e.target.checked ? "on" : "off");
            }}
          />
          <span>Desktop Alerts</span>
        </label>
        <label className="settings-check">
          <input
            type="checkbox"
            checked={morningDigest}
            onChange={(e) => {
              setMorningDigest(e.target.checked);
              localStorage.setItem("atw-hub-digest", e.target.checked ? "on" : "off");
            }}
          />
          <span>Morning Digest</span>
        </label>
        <p className="note">Saved on this Mac. This beta does not send notifications.</p>
      </section>

      <section className="settings-card" id="notifications-mail">
        <div className="settings-card-head">
          <h3>Mail</h3>
          <span className="pill">Soon</span>
        </div>
        <p className="muted">
          Per-folder alerts, VIP senders, and quiet hours will appear here.
        </p>
      </section>

      <section className="settings-card" id="notifications-chat">
        <div className="settings-card-head">
          <h3>Chat</h3>
          <span className="pill">Soon</span>
        </div>
        <p className="muted">
          Mentions, DMs, and channel mute defaults will be configurable when Chat notifications go
          live.
        </p>
      </section>
    </div>
  );
}

const MAIL_SETTINGS_SECTIONS = [
  { id: "compose", label: "Compose", path: "/settings/mail" },
  { id: "reading", label: "Reading", path: "/settings/mail/reading" },
  { id: "list", label: "List", path: "/settings/mail/list" },
  { id: "folders", label: "Folders", path: "/settings/mail/folders" },
  { id: "storage", label: "Storage", path: "/settings/mail/storage" },
  { id: "more", label: "More", path: "/settings/mail/more" },
] as const;

type MailSettingsSectionId = (typeof MAIL_SETTINGS_SECTIONS)[number]["id"];

function isMailSettingsSection(value: string | undefined): value is MailSettingsSectionId {
  return MAIL_SETTINGS_SECTIONS.some((section) => section.id === value);
}

function SignatureSourceEditor({
  accountId,
  email,
  kind,
  kicker,
  note,
  htmlMode,
  onToggleHtml,
  value,
  onChange,
  onImageRejected,
}: {
  accountId: string;
  email: string;
  kind: "new" | "reply";
  kicker: string;
  note: string;
  htmlMode: boolean;
  onToggleHtml: () => void;
  value: string;
  onChange: (html: string) => void;
  onImageRejected: (message: string) => void;
}) {
  const field = kind === "new" ? "New message" : "Reply";
  return (
    <div>
      <div className="settings-signature-kicker-row">
        <p className="settings-signature-kicker">{kicker}</p>
        <button
          type="button"
          className={`secondary-btn settings-signature-html-btn${htmlMode ? " is-on" : ""}`}
          aria-pressed={htmlMode}
          onClick={onToggleHtml}
        >
          HTML
        </button>
      </div>
      <p className="note">{note}</p>
      {htmlMode ? (
        <textarea
          className="settings-signature-html"
          aria-label={`${field} signature HTML for ${email}`}
          spellCheck={false}
          value={value}
          placeholder="Paste signature HTML"
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <MailComposeEditor
          key={`${accountId}:${kind}`}
          className="settings-signature-editor"
          autoFocus={false}
          imageMaxEdge={480}
          label={`${field} signature for ${email}`}
          toolbarLabel={`${field} signature formatting`}
          placeholder={
            kind === "new" ? "Name, title, phone, or a logo" : "A shorter signature for replies"
          }
          value={value}
          onChange={onChange}
          onImageRejected={onImageRejected}
        />
      )}
    </div>
  );
}

export function SettingsMailPage() {
  const { section: sectionParam } = useParams();
  const navigate = useNavigate();
  const [sectionMenuOpen, setSectionMenuOpen] = useState(false);
  const invalidSection = Boolean(sectionParam && !isMailSettingsSection(sectionParam));
  const section: MailSettingsSectionId = isMailSettingsSection(sectionParam) ? sectionParam : "compose";
  const activeSection =
    MAIL_SETTINGS_SECTIONS.find((item) => item.id === section) ?? MAIL_SETTINGS_SECTIONS[0];
  const [folderView, setFolderView] = useState<MailViewId>(() => readMailView());
  const [conversations, setConversations] = useState(() => readMailConversations());
  const [threadOrder, setThreadOrder] = useState<MailThreadOrderId>(() => readMailThreadOrder());
  const [swipeLeft, setSwipeLeft] = useState<MailSwipeActionId>(() => readMailSwipeLeft());
  const [swipeRight, setSwipeRight] = useState<MailSwipeActionId>(() => readMailSwipeRight());
  const [cacheWindow, setCacheWindow] = useState(() =>
    CACHE_WINDOWS.some((v) => v.id === readMailCacheWindowId())
      ? readMailCacheWindowId()
      : DEFAULT_MAIL_CACHE_WINDOW,
  );
  const [notice, setNotice] = useState("");
  const [lineSpacing, setLineSpacing] = useState<MailLineSpacingId>(() => readMailLineSpacing());
  const [mailAccounts, setMailAccounts] = useState<MailAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [folderSyncTick, setFolderSyncTick] = useState(0);
  const [signatureOn, setSignatureOn] = useState(() => readMailSignatureEnabled());
  const [signatureAccountId, setSignatureAccountId] = useState("");
  const [createHtml, setCreateHtml] = useState("");
  const [replyHtml, setReplyHtml] = useState("");
  const [savedCreate, setSavedCreate] = useState("");
  const [savedReply, setSavedReply] = useState("");
  const [createAsHtml, setCreateAsHtml] = useState(false);
  const [replyAsHtml, setReplyAsHtml] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadAccounts() {
      setAccountsLoading(true);
      try {
        const list = await fetchMailAccounts();
        if (!cancelled) setMailAccounts(list.filter((account) => mailConnected(account)));
      } catch {
        if (!cancelled) setMailAccounts([]);
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    }
    void loadAccounts();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (accountsLoading) return;
    if (signatureAccountId && mailAccounts.some((account) => account.id === signatureAccountId)) {
      return;
    }
    const nextId = mailAccounts[0]?.id || "";
    if (!nextId) return;
    const pair = signatureEditorPair(nextId);
    setSignatureAccountId(nextId);
    setCreateHtml(pair.create);
    setReplyHtml(pair.reply);
    setSavedCreate(pair.create);
    setSavedReply(pair.reply);
  }, [accountsLoading, mailAccounts, signatureAccountId]);

  useEffect(() => {
    function onSyncChange() {
      setFolderSyncTick((tick) => tick + 1);
    }
    window.addEventListener(MAIL_SYNC_FOLDERS_EVENT, onSyncChange);
    window.addEventListener("storage", onSyncChange);
    return () => {
      window.removeEventListener(MAIL_SYNC_FOLDERS_EVENT, onSyncChange);
      window.removeEventListener("storage", onSyncChange);
    };
  }, []);

  void folderSyncTick;

  function onCacheChange(value: string) {
    setCacheWindow(value);
    localStorage.setItem("atw-hub-mail-cache-window", value);
    setNotice("Mail cache window updated.");
  }

  const signatureAccount = mailAccounts.find((account) => account.id === signatureAccountId);
  const signatureEmail = signatureAccount?.email || "";
  const signatureDirty = createHtml !== savedCreate || replyHtml !== savedReply;

  function showSignatureAccount(accountId: string) {
    const pair = signatureEditorPair(accountId);
    setSignatureAccountId(accountId);
    setCreateHtml(pair.create);
    setReplyHtml(pair.reply);
    setSavedCreate(pair.create);
    setSavedReply(pair.reply);
  }

  useEffect(() => {
    function onSignatures() {
      setSignatureOn(readMailSignatureEnabled());
      if (!signatureAccountId) return;
      if (createHtml !== savedCreate || replyHtml !== savedReply) return;
      const pair = signatureEditorPair(signatureAccountId);
      setCreateHtml(pair.create);
      setReplyHtml(pair.reply);
      setSavedCreate(pair.create);
      setSavedReply(pair.reply);
    }
    window.addEventListener(MAIL_SIGNATURE_EVENT, onSignatures);
    return () => window.removeEventListener(MAIL_SIGNATURE_EVENT, onSignatures);
  }, [signatureAccountId, createHtml, replyHtml, savedCreate, savedReply]);

  async function saveSignatures() {
    if (!signatureAccountId || !signatureEmail) return;
    if (createHtml.length > 180_000 || replyHtml.length > 180_000) {
      setNotice("That image is too large for a signature. Use a smaller logo.");
      return;
    }
    try {
      await saveMailSignaturePair(signatureAccountId, signatureEmail, {
        create: createHtml,
        reply: replyHtml,
      });
    } catch {
      setNotice("Could not save signatures. Try again.");
      return;
    }
    const pair = signatureEditorPair(signatureAccountId);
    setCreateHtml(pair.create);
    setReplyHtml(pair.reply);
    setSavedCreate(pair.create);
    setSavedReply(pair.reply);
    setNotice(`Signatures saved for ${signatureEmail}.`);
  }

  if (invalidSection) return <Navigate to="/settings/mail" replace />;

  return (
    <div className="settings-area">
      <h2 className="settings-area-title">Mail</h2>

      <nav className="connections-tabs desktop-only" aria-label="Mail sections">
        {MAIL_SETTINGS_SECTIONS.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            end={item.id === "compose"}
            className={({ isActive }) => cn("connections-tab", isActive && "active")}
          >
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="connections-section-select phone-only">
        <button
          type="button"
          className="connections-section-trigger"
          aria-expanded={sectionMenuOpen}
          aria-haspopup="listbox"
          aria-label="Mail section"
          onClick={() => setSectionMenuOpen((open) => !open)}
        >
          <span className="connections-section-trigger-label">{activeSection.label}</span>
          <ChevronDown size={16} aria-hidden />
        </button>
        {sectionMenuOpen ? (
          <>
            <button
              type="button"
              className="connections-section-scrim"
              aria-label="Close Mail sections"
              onClick={() => setSectionMenuOpen(false)}
            />
            <div className="connections-section-menu" role="listbox" aria-label="Mail sections">
              {MAIL_SETTINGS_SECTIONS.map((item) => {
                const selected = item.id === section;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={cn("connections-section-option", selected && "active")}
                    onClick={() => {
                      setSectionMenuOpen(false);
                      if (!selected) navigate(item.path);
                    }}
                  >
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </>
        ) : null}
      </div>

      {notice ? <p className="banner-ok">{notice}</p> : null}

      {section === "compose" ? (
      <>
      <section className="settings-card" id="mail-spacing">
        <div className="settings-card-head">
          <h3>Line spacing</h3>
        </div>
        <p className="muted">
          Standard is the normal email spacing. Compact is tighter. Relaxed adds more room between
          lines. This is also on the compose toolbar.
        </p>
        <div className="segmented" role="radiogroup" aria-label="Line spacing">
          {MAIL_LINE_SPACING_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={lineSpacing === opt.id}
              className={lineSpacing === opt.id ? "active" : ""}
              onClick={() => {
                setLineSpacing(opt.id);
                writeMailLineSpacing(opt.id);
                setNotice(`Line spacing set to ${opt.label}.`);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      <section className="settings-card" id="mail-signature">
        <div className="settings-card-head">
          <h3>Signature</h3>
          <span className="pill">
            {accountsLoading
              ? "Loading…"
              : mailAccounts.length === 0
                ? "None Connected"
                : `${mailAccounts.length} Connected`}
          </span>
        </div>
        <p className="muted">
          Choose a mailbox, then set one signature for new emails and another for replies.
          Bold, lists, links, and images work in both. HTML opens the source so you can paste a
          finished signature. Saved on your account, so it is here the next time you sign in.
        </p>
        <label className="settings-check">
          <input
            type="checkbox"
            checked={signatureOn}
            onChange={(e) => {
              const on = e.target.checked;
              setSignatureOn(on);
              void saveMailSignatureEnabled(on)
                .then(() => {
                  setNotice(on ? "Signatures will be added when you compose." : "Signatures turned off.");
                })
                .catch(() => {
                  setSignatureOn(!on);
                  setNotice("Could not save that setting. Try again.");
                });
            }}
          />
          <span>
            <strong>Include signatures</strong>
            <span className="muted block-note">
              Turn this off to compose without a signature. Each mailbox keeps its own.
            </span>
          </span>
        </label>
        {accountsLoading ? <p className="muted">Loading mailboxes…</p> : null}
        {!accountsLoading && mailAccounts.length === 0 ? (
          <div className="connections-empty">
            <p className="muted">Connect a mailbox before adding a signature.</p>
            <div className="settings-btn-row">
              <Link className="primary-btn settings-link-btn" to="/settings/connections/mail">
                Connect Mail
              </Link>
            </div>
          </div>
        ) : null}
        {!accountsLoading && mailAccounts.length > 0 ? (
          <label>
            Mailbox
            <select
              value={signatureAccountId}
              aria-label="Mailbox"
              onChange={(e) => {
                const nextId = e.target.value;
                if (nextId === signatureAccountId) return;
                if (
                  signatureDirty &&
                  !window.confirm("Discard unsaved signature changes for this mailbox?")
                ) {
                  return;
                }
                showSignatureAccount(nextId);
              }}
            >
              {mailAccounts.map((account) => {
                const name = resolveAccountLabel(account);
                const label =
                  name && name !== account.email ? `${account.email} (${name})` : account.email;
                return (
                  <option key={account.id} value={account.id}>
                    {label}
                  </option>
                );
              })}
            </select>
          </label>
        ) : null}
        {signatureAccount && signatureEmail ? (
          <div className="settings-signature-panel">
            <SignatureSourceEditor
              accountId={signatureAccount.id}
              email={signatureEmail}
              kind="new"
              kicker="New messages"
              note={`Added when you start a new email from ${signatureEmail}.`}
              htmlMode={createAsHtml}
              onToggleHtml={() => setCreateAsHtml((on) => !on)}
              value={createHtml}
              onChange={setCreateHtml}
              onImageRejected={(message) => setNotice(message)}
            />
            <SignatureSourceEditor
              accountId={signatureAccount.id}
              email={signatureEmail}
              kind="reply"
              kicker="Replies and forwards"
              note={`Added when you reply or forward from ${signatureEmail}.`}
              htmlMode={replyAsHtml}
              onToggleHtml={() => setReplyAsHtml((on) => !on)}
              value={replyHtml}
              onChange={setReplyHtml}
              onImageRejected={(message) => setNotice(message)}
            />
            <div className="settings-btn-row">
              <button
                type="button"
                className="primary-btn settings-link-btn"
                disabled={!signatureDirty}
                onClick={saveSignatures}
              >
                Save
              </button>
              {signatureDirty ? <span className="note">Unsaved changes</span> : null}
            </div>
          </div>
        ) : null}
      </section>
      </>
      ) : null}

      {section === "reading" ? (
      <section className="settings-card" id="mail-view-type">
        <div className="settings-card-head">
          <h3>View type</h3>
          <span className="pill">Desktop</span>
        </div>
        <p className="muted">
          Choose how the Mail left column shows accounts and folders. Default is Folder Tree.
        </p>
        <label>
          Folder View
          <select
            value={folderView}
            aria-label="Folder view"
            onChange={(e) => {
              const next = e.target.value as MailViewId;
              setFolderView(next);
              writeMailView(next);
              setNotice("Mail view type updated.");
            }}
          >
            {FOLDER_VIEWS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="settings-check">
          <input
            type="checkbox"
            checked={conversations}
            onChange={(e) => {
              setConversations(e.target.checked);
              writeMailConversations(e.target.checked);
              setNotice(
                e.target.checked
                  ? "Conversations on — replies group into one list row."
                  : "Conversations off — each reply is its own list row.",
              );
            }}
          />
          <span>
            <strong>Show Conversations in the Message List</strong>
            <span className="muted block-note">
              When conversations are on, Hub groups replies into one list row. Turn this off to list
              each reply separately. Opening a thread always shows every email in the chain; use
              the chevron to expand or collapse each body.
            </span>
          </span>
        </label>
        <label>
          Thread Order
          <select
            value={threadOrder}
            aria-label="Mail thread order"
            onChange={(e) => {
              const next = e.target.value as MailThreadOrderId;
              setThreadOrder(next);
              writeMailThreadOrder(next);
              setNotice("Thread order updated.");
            }}
          >
            {THREAD_ORDERS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <p className="note">Order of messages inside an open conversation. Default is newest first.</p>
      </section>
      ) : null}

      {section === "list" ? (
      <section className="settings-card" id="mail-list-swipe">
        <div className="settings-card-head">
          <h3>List swipe actions</h3>
          <span className="pill">Desktop</span>
        </div>
        <p className="muted">
          Drag a message left or right in the Mail list (mouse or trackpad). Choose what each
          direction does — or Nothing. Defaults: left = Mark Read / Unread, right = Archive.
        </p>
        <label>
          Swipe left
          <select
            value={swipeLeft}
            aria-label="Swipe left action"
            onChange={(e) => {
              const next = e.target.value as MailSwipeActionId;
              setSwipeLeft(next);
              writeMailSwipeLeft(next);
              setNotice("Swipe left action updated.");
            }}
          >
            {MAIL_SWIPE_ACTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Swipe right
          <select
            value={swipeRight}
            aria-label="Swipe right action"
            onChange={(e) => {
              const next = e.target.value as MailSwipeActionId;
              setSwipeRight(next);
              writeMailSwipeRight(next);
              setNotice("Swipe right action updated.");
            }}
          >
            {MAIL_SWIPE_ACTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <p className="note">
          Available now: Nothing, Mark Read / Unread, Mark Read, Mark Unread, Archive, Delete,
          Star / Unstar. Read and unread sync to the connected account via Hub API.
        </p>
      </section>
      ) : null}

      {section === "folders" ? (
      <section className="settings-card" id="mail-folders-connected">
        <div className="settings-card-head">
          <h3>Folders from connected accounts</h3>
          <span className="pill">Per Account</span>
        </div>
        <p className="muted">
          Off by default. Hub keeps Inbox, Sent, Drafts, and Archive for every mailbox. Turn sync
          on for an account to pull that provider's folders and labels into the Mail nav — only
          for accounts you choose.
        </p>
        {accountsLoading ? <p className="muted">Loading accounts…</p> : null}
        {!accountsLoading && mailAccounts.length === 0 ? (
          <p className="note">
            No connected mail accounts yet.{" "}
            <Link to="/settings/connections/mail">Connect Mail</Link> first, then enable sync
            here or on the Connected tab.
          </p>
        ) : null}
        {!accountsLoading && mailAccounts.length > 0 ? (
          <ul className="account-list mail-folder-sync-list">
            {mailAccounts.map((account) => {
              const enabled = isAccountFolderSyncEnabled(account.id);
              const label = resolveAccountLabel(account);
              return (
                <li key={account.id} className="account-row">
                  <label className="settings-check compact">
                    <input
                      type="checkbox"
                      checked={enabled}
                      aria-label={`Sync folders for ${label}`}
                      onChange={(e) => {
                        setAccountFolderSyncEnabled(account.id, e.target.checked);
                        setFolderSyncTick((tick) => tick + 1);
                        setNotice(
                          e.target.checked
                            ? `Syncing folders for ${label}.`
                            : `Using standard folders for ${label}.`,
                        );
                      }}
                    />
                    <span>
                      <strong className="mail-email-truncate" title={account.email}>
                        {label}
                      </strong>
                      {label !== account.email ? (
                        <span className="muted block-note">{account.email}</span>
                      ) : null}
                      <span className="muted block-note">
                        {enabled
                          ? "Provider folders and labels in Mail nav."
                          : "Standard Inbox / Sent / Drafts / Archive only."}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        ) : null}
        <p className="note">
          All Inboxes always uses the standard four folders. Mapping Hub folders to provider
          folders lands later only when a label does not fit Inbox / Sent / Drafts / Archive.
        </p>
      </section>
      ) : null}

      {section === "storage" ? (
      <section className="settings-card" id="mail-cache">
        <div className="settings-card-head">
          <h3>Mail Cache</h3>
          <span className="pill accent-pill">Cached Mode</span>
        </div>
        <p className="muted">
          Keep recent mail, including full messages, on this device so Mail opens instantly
          and works offline. Hub checks for new mail while it is open. Default: 30 days.
        </p>
        <label>
          Keep Mail For
          <select
            value={cacheWindow}
            aria-label="Keep mail for"
            onChange={(e) => onCacheChange(e.target.value)}
          >
            {CACHE_WINDOWS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <p className="note">
          Mail is stored only on this device, never in ATW&apos;s cloud. Longer windows download
          older mail in the background and use more device storage.
        </p>
      </section>
      ) : null}

      {section === "more" ? (
      <section className="settings-card" id="mail-more">
        <div className="settings-card-head">
          <h3>More</h3>
          <span className="pill">Soon</span>
        </div>
        <p className="muted">
          Rules and the reading pane will land here.
        </p>
      </section>
      ) : null}
    </div>
  );
}

export function SettingsChatPage() {
  return (
    <div className="settings-area">
      <h2 className="settings-area-title">Chat</h2>

      <section className="settings-card" id="chat-general">
        <div className="settings-card-head">
          <h3>General</h3>
          <span className="pill">Soon</span>
        </div>
        <p className="muted">
          Chat notification sounds, sidebar density, and thread defaults will appear here.
        </p>
      </section>

      <section className="settings-card" id="chat-presence">
        <div className="settings-card-head">
          <h3>Presence</h3>
          <span className="pill">Soon</span>
        </div>
        <p className="muted">
          Away status and automatic status messages are not configurable in this beta.
        </p>
      </section>
    </div>
  );
}

const CAL_VIEWS = ["1day", "3day", "workweek", "week", "2weeks", "month"] as const;

export function SettingsCalendarPage() {
  const [phoneView, setPhoneView] = useState(() =>
    readString("atw-hub-cal-view-phone", "3day", [...CAL_VIEWS]),
  );
  const [desktopView, setDesktopView] = useState(() =>
    readString("atw-hub-cal-view-desktop", "week", [...CAL_VIEWS]),
  );
  const [weekStart, setWeekStart] = useState(() => {
    const n = Number.parseInt(readString("atw-hub-cal-week-start", "1"), 10);
    return Number.isInteger(n) && n >= 0 && n <= 6 ? n : 1;
  });
  const [workDays, setWorkDays] = useState<number[]>(() => {
    const stored = readJson<number[]>("atw-hub-cal-work-days", [1, 2, 3, 4, 5]);
    return Array.isArray(stored) && stored.length ? stored : [1, 2, 3, 4, 5];
  });

  function toggleWorkDay(day: number) {
    setWorkDays((prev) => {
      const next = prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort();
      localStorage.setItem("atw-hub-cal-work-days", JSON.stringify(next.length ? next : [1]));
      return next.length ? next : [1];
    });
  }

  return (
    <div className="settings-area">
      <h2 className="settings-area-title">Calendar</h2>

      <section className="settings-card" id="calendar-default-view">
        <div className="settings-card-head">
          <h3>Default view</h3>
        </div>
        <p className="muted">
          Phones open on 3 Day. Desktop opens on a full week. Change either one if you want a different
          starting view.
        </p>
        <label>
          Phone
          <select
            value={phoneView}
            aria-label="Phone calendar view"
            onChange={(e) => {
              setPhoneView(e.target.value);
              localStorage.setItem("atw-hub-cal-view-phone", e.target.value);
              window.dispatchEvent(new Event("atw-hub-cal-view"));
            }}
          >
            <option value="1day">1 Day</option>
            <option value="3day">3 Day</option>
            <option value="workweek">Work Week</option>
            <option value="week">Week</option>
            <option value="2weeks">2 Weeks</option>
            <option value="month">Month</option>
          </select>
        </label>
        <label>
          Desktop
          <select
            value={desktopView}
            aria-label="Desktop calendar view"
            onChange={(e) => {
              setDesktopView(e.target.value);
              localStorage.setItem("atw-hub-cal-view-desktop", e.target.value);
              window.dispatchEvent(new Event("atw-hub-cal-view"));
            }}
          >
            <option value="1day">1 Day</option>
            <option value="3day">3 Day</option>
            <option value="workweek">Work Week</option>
            <option value="week">Week</option>
            <option value="2weeks">2 Weeks</option>
            <option value="month">Month</option>
          </select>
        </label>
      </section>

      <section className="settings-card" id="calendar-week">
        <div className="settings-card-head">
          <h3>Start of week</h3>
        </div>
        <label>
          First Day of the Week
          <select
            value={String(weekStart)}
            aria-label="First day of the week"
            onChange={(e) => {
              const n = Number.parseInt(e.target.value, 10);
              setWeekStart(n);
              localStorage.setItem("atw-hub-cal-week-start", String(n));
              window.dispatchEvent(new Event("atw-hub-cal-week-start"));
            }}
          >
            {WEEKDAYS.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
        <p className="note">
          Used by Week, Work Week, 2 Weeks, and the month grid. 1 Day and 3 Day still follow the
          selected date, with the same weekday order. Default is Monday.
        </p>
      </section>

      <section className="settings-card" id="calendar-work-week">
        <div className="settings-card-head">
          <h3>Work week</h3>
        </div>
        <p className="muted">Choose which weekdays count as the work week for Calendar.</p>
        <div className="work-week-grid" role="group" aria-label="Work week days">
          {WEEKDAYS.map((d) => (
            <label key={d.id} className="settings-check compact">
              <input
                type="checkbox"
                checked={workDays.includes(d.id)}
                onChange={() => toggleWorkDay(d.id)}
              />
              <span>{d.label}</span>
            </label>
          ))}
        </div>
        <p className="note">Used by the Work Week view on Calendar. Default is Monday through Friday.</p>
      </section>
    </div>
  );
}

export function SettingsMeetingsPage() {
  return (
    <div className="settings-area">
      <h2 className="settings-area-title">Meetings</h2>

      <section className="settings-card" id="meetings-general">
        <div className="settings-card-head">
          <h3>Meetings</h3>
        </div>
        <p className="muted">
          Google Meet and Zoom show up together in Meetings. Connect them under Connections.
        </p>
      </section>
    </div>
  );
}
